import { useState } from "react"
import {
  X,
  Calendar,
  Clock,
  Send,
  CheckCircle,
  User,
  AlertCircle,
  RefreshCw,
} from "lucide-react"
import interviewTrackingService from "../services/interviewTrackingService"

// ── Shared style tokens (matching OrgScheduleInterviewForm) ──────────────────
const inputClass =
  "w-full px-3 py-2 h-10 border border-zinc-200 text-[14px] font-medium text-zinc-800 placeholder-zinc-500 focus:outline-none focus:border-zinc-500 transition-colors bg-white hover:border-zinc-300"

const labelClass =
  "block text-[11px] font-bold text-zinc-400 uppercase tracking-widest mb-1.5"

const ScheduleNextRoundModal = ({
  isOpen,
  onClose,
  interview,
  interviewersData,
  orgId,
}) => {
  const [date, setDate] = useState("")
  const [time, setTime] = useState("")
  const [title, setTitle] = useState(
    `${interview?.title || "Interview"} - Next Round`,
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)
  const [selectedInterviewer, setSelectedInterviewer] = useState("")

  if (!isOpen) return null

  const today = new Date().toISOString().split("T")[0]

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!date || !time || !title) {
      setError("Please fill in all required fields")
      return
    }

    if (!selectedInterviewer) {
      setError("Please select an interviewer")
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const selectedInterviewerObj = interviewersData.find(
        (interviewer) => interviewer.email === selectedInterviewer,
      )

      const nextRoundData = {
        date,
        time,
        title,
        selectedInterviewerId: selectedInterviewer,
        interviewerName: selectedInterviewerObj?.email || "Interviewer",
        interviewerEmail: selectedInterviewerObj?.email || selectedInterviewer,
        interviewerUid: selectedInterviewerObj?.uid || "",
      }

      const result = await interviewTrackingService.scheduleNextRound(
        interview,
        nextRoundData,
        orgId,
      )

      if (!result) {
        throw new Error("Failed to schedule next round interview")
      }

      setSuccess(true)
    } catch (error) {
      console.error("Error scheduling next round:", error)
      setError("Failed to schedule the next round. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 backdrop-blur-sm"
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      <style>{`@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');`}</style>

      <div
        className="bg-white w-full max-w-xl border border-zinc-200 shadow-2xl flex flex-col max-h-[92vh]"
        style={{ borderRadius: 0 }}
      >
        {/* ── Header ── */}
        <div className="flex justify-between items-center px-7 py-4 border-b border-zinc-200 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-1 h-6 bg-indigo-500" />
            <div>
              <h2 className="text-[13px] font-bold uppercase tracking-widest text-zinc-900">
                Schedule Next Round
              </h2>
              {interview?.title && (
                <p
                  className="text-[13px] text-zinc-700 mt-0.5"
                  style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                >
                  {interview.title}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-7 h-7 border border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50 text-zinc-400 hover:text-zinc-700 transition-all"
            style={{ borderRadius: 0 }}
          >
            <X size={14} />
          </button>
        </div>

        {/* ── Form body ── */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="px-7 py-5 overflow-y-auto flex-1 space-y-5">
            {/* Success banner */}
            {success && (
              <div
                className="flex items-start gap-2.5 px-4 py-3 bg-green-50 border border-green-200 text-green-700 text-[12px] font-medium"
                style={{ borderRadius: 0 }}
              >
                <CheckCircle size={13} className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold">Next round scheduled successfully</p>
                  <p className="text-green-600 mt-0.5">
                    An invitation will be sent to the candidate.
                  </p>
                </div>
              </div>
            )}

            {/* Error banner */}
            {error && (
              <div
                className="flex items-center gap-2.5 px-4 py-3 bg-red-50 border border-red-200 text-red-600 text-[12px] font-medium"
                style={{ borderRadius: 0 }}
              >
                <AlertCircle size={13} className="flex-shrink-0" />
                {error}
              </div>
            )}

            {/* ── Interview Title ── */}
            <div>
              <label htmlFor="interviewTitle" className={labelClass}>
                Interview Title
              </label>
              <input
                type="text"
                id="interviewTitle"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={inputClass}
                style={{ borderRadius: 0 }}
                required
              />
            </div>

            {/* ── Interviewer Select ── */}
            <div>
              <label htmlFor="interviewerSelect" className={labelClass}>
                <User size={11} className="inline mr-1 -mt-0.5" />
                Select Interviewer*
              </label>
              <div className="relative">
                <select
                  id="interviewerSelect"
                  value={selectedInterviewer}
                  onChange={(e) => setSelectedInterviewer(e.target.value)}
                  className={`${inputClass} appearance-none pr-8`}
                  style={{ borderRadius: 0 }}
                  required
                >
                  <option value="">Select an interviewer</option>
                  {interviewersData?.map((interviewer) => (
                    <option
                      key={interviewer.uid || `interviewer-${Math.random()}`}
                      value={interviewer.email}
                    >
                      {interviewer.email || "Interviewer"}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    className="text-zinc-400"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
              </div>
            </div>

            {/* ── Date & Time ── */}
            <div>
              <p className={labelClass}>Date & Time</p>
              <div
                className="grid grid-cols-2 gap-0 border border-zinc-200"
                style={{ borderRadius: 0 }}
              >
                {/* Date */}
                <div className="border-r border-zinc-200">
                  <div className="px-3 py-1.5 border-b border-zinc-100">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                      Date
                    </span>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <Calendar size={13} className="text-zinc-300" />
                    </div>
                    <input
                      type="date"
                      id="interviewDate"
                      min={today}
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      required
                      className="w-full pl-9 pr-3 py-2 h-10 text-[13px] text-zinc-900 bg-white focus:outline-none focus:bg-zinc-50 transition-colors border-0"
                      style={{ borderRadius: 0 }}
                    />
                  </div>
                </div>

                {/* Time */}
                <div>
                  <div className="px-3 py-1.5 border-b border-zinc-100">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                      Time
                    </span>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <Clock size={13} className="text-zinc-300" />
                    </div>
                    <input
                      type="time"
                      id="interviewTime"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      required
                      className="w-full pl-9 pr-3 py-2 h-10 text-[13px] text-zinc-900 bg-white focus:outline-none focus:bg-zinc-50 transition-colors border-0"
                      style={{ borderRadius: 0 }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* ── Candidate preview ── */}
            <div
              className="px-4 py-3 bg-zinc-50 border border-zinc-200"
              style={{ borderRadius: 0 }}
            >
              <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1">
                Candidate
              </p>
              <p className="text-[13px] font-semibold text-zinc-800">
                {interview.candidateName || "Candidate"}
                <span className="font-normal text-zinc-500 ml-1.5">
                  ({interview.candidateEmail || "No email"})
                </span>
              </p>
            </div>
          </div>

          {/* ── Footer ── */}
          <div className="px-7 py-4 border-t border-zinc-200 flex items-center justify-between shrink-0 bg-zinc-50">
            <p
              className="text-[13px] text-zinc-400"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              🔁 Next round
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting || success}
                className="px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-zinc-600 border border-zinc-200 hover:border-zinc-400 hover:text-zinc-900 transition-colors disabled:opacity-40"
                style={{ borderRadius: 0 }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || success}
                className="flex items-center gap-2 px-5 py-2 text-[11px] font-bold uppercase tracking-widest bg-zinc-900 text-white hover:bg-zinc-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-zinc-900"
                style={{ borderRadius: 0 }}
              >
                {isSubmitting ? (
                  <>
                    <RefreshCw size={12} className="animate-spin" />
                    Scheduling…
                  </>
                ) : (
                  <>
                    <Send size={12} strokeWidth={2.5} />
                    Schedule Interview
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ScheduleNextRoundModal
